import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from "react";
import { roomTaskService, roomSocketService } from "../services/rooms";
import { toast } from "react-toastify";

const RoomTasksContext = createContext();

export const useRoomTasks = () => {
  const context = useContext(RoomTasksContext);
  if (!context) {
    throw new Error("useRoomTasks must be used within a RoomTasksProvider");
  }
  return context;
};

export const RoomTasksProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [kanbanBoard, setKanbanBoard] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentRoomId, setCurrentRoomId] = useState(null);

  /**
   * Fetch all tasks for a room
   */
  const fetchTasks = useCallback(async (roomId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomTaskService.getRoomTasks(roomId);

      if (response.success) {
        setTasks(response.data || []);
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch current user's tasks in a room
   */
  const fetchMyTasks = useCallback(async (roomId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomTaskService.getMyTasks(roomId);

      if (response.success) {
        setMyTasks(response.data || []);
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error("Failed to load your tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch Kanban board for a room
   */
  const fetchKanbanBoard = useCallback(async (roomId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomTaskService.getKanbanBoard(roomId);

      if (response.success) {
        setKanbanBoard(response.data || {});
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error("Failed to load Kanban board");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch task statistics for a room
   */
  const fetchTaskStats = useCallback(async (roomId) => {
    try {
      const response = await roomTaskService.getTaskStats(roomId);

      if (response.success) {
        setStats(response.data);
        return response;
      }
    } catch (err) {
toast.error("Failed to load task statistics");
    }
  }, []);

  /**
   * Create a new task (Leader only)
   */
  const createTask = useCallback(
    async (taskData) => {
      try {
setLoading(true);
        setError(null);
        const response = await roomTaskService.createTask(taskData);
if (response.success) {
          toast.success("Task created successfully!");

          // Notify via socket
          if (currentRoomId) {
            roomSocketService.notifyTaskCreated(currentRoomId, response.data);
          }

          // Refresh Kanban board
          if (currentRoomId) {
            await fetchKanbanBoard(currentRoomId);
          }

          return response.data;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to create task");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoomId, fetchKanbanBoard]
  );

  /**
   * Update a task (Leader only)
   */
  const updateTask = useCallback(
    async (taskId, updates) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomTaskService.updateTask(taskId, updates);

        if (response.success) {
          toast.success("Task updated successfully!");

          // Notify via socket
          if (currentRoomId) {
            roomSocketService.notifyTaskUpdated(currentRoomId, taskId, updates);
          }

          // Refresh Kanban board
          if (currentRoomId) {
            await fetchKanbanBoard(currentRoomId);
          }

          return response.data;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to update task");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoomId, fetchKanbanBoard]
  );

  /**
   * Update task status (Leader only)
   */
  const updateTaskStatus = useCallback(
    async (taskId, status) => {
      try {
        const response = await roomTaskService.updateTaskStatus(taskId, status);

        if (response.success) {
          toast.success("Task status updated!");

          // Notify via socket
          if (currentRoomId) {
            roomSocketService.notifyTaskStatusChanged(
              currentRoomId,
              taskId,
              status
            );
          }

          // Optimistically update Kanban board
          if (currentRoomId) {
            await fetchKanbanBoard(currentRoomId);
          }

          return response.data;
        }
      } catch (err) {
toast.error(err.message || "Failed to update task status");
        throw err;
      }
    },
    [currentRoomId, fetchKanbanBoard]
  );

  /**
   * Delete a task (Leader only)
   */
  const deleteTask = useCallback(
    async (taskId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomTaskService.deleteTask(taskId);

        if (response.success) {
          toast.success("Task deleted successfully!");

          // Refresh Kanban board
          if (currentRoomId) {
            await fetchKanbanBoard(currentRoomId);
          }

          return response;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to delete task");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoomId, fetchKanbanBoard]
  );

  /**
   * Toggle subtask completion
   */
  const toggleSubtask = useCallback(
    async (taskId, subtaskId) => {
      try {
        const response = await roomTaskService.toggleSubtask(taskId, subtaskId);

        if (response.success) {
          // Update task in state with new subtask data and progress
          setTasks((prevTasks) =>
            prevTasks.map((task) =>
              task._id === taskId
                ? {
                    ...task,
                    subtasks: task.subtasks.map((sub) =>
                      sub._id === subtaskId ? response.data.subtask : sub
                    ),
                    progress: response.data.progress,
                  }
                : task
            )
          );

          // Also update in Kanban board if present
          if (kanbanBoard) {
            setKanbanBoard((prevBoard) => {
              const updatedBoard = { ...prevBoard };
              Object.keys(updatedBoard).forEach((status) => {
                updatedBoard[status] = updatedBoard[status].map((task) =>
                  task._id === taskId
                    ? {
                        ...task,
                        subtasks: task.subtasks.map((sub) =>
                          sub._id === subtaskId ? response.data.subtask : sub
                        ),
                        progress: response.data.progress,
                      }
                    : task
                );
              });
              return updatedBoard;
            });
          }

          return response.data;
        }
      } catch (err) {
toast.error(err.message || "Failed to toggle subtask");
        throw err;
      }
    },
    [kanbanBoard]
  );

  /**
   * Add a comment to a task
   */
  const addTaskComment = useCallback(
    async (taskId, content) => {
      try {
        const response = await roomTaskService.addComment(taskId, content);

        if (response.success) {
          toast.success("Comment added!");

          // Update task in state with new comments
          setTasks((prevTasks) =>
            prevTasks.map((task) =>
              task._id === taskId ? { ...task, comments: response.data } : task
            )
          );

          // Also update in Kanban board if present
          if (kanbanBoard) {
            setKanbanBoard((prevBoard) => {
              const updatedBoard = { ...prevBoard };
              Object.keys(updatedBoard).forEach((status) => {
                updatedBoard[status] = updatedBoard[status].map((task) =>
                  task._id === taskId
                    ? { ...task, comments: response.data }
                    : task
                );
              });
              return updatedBoard;
            });
          }

          return response.data;
        }
      } catch (err) {
toast.error(err.message || "Failed to add comment");
        throw err;
      }
    },
    [kanbanBoard]
  );

  /**
   * Attach a file to a task
   */
  const attachFileToTask = useCallback(
    async (taskId, attachmentId) => {
      try {
        const response = await roomTaskService.attachFile(taskId, attachmentId);

        if (response.success) {
          toast.success("File attached to task!");

          // Update task in state with new attachments
          setTasks((prevTasks) =>
            prevTasks.map((task) =>
              task._id === taskId
                ? { ...task, attachments: response.data }
                : task
            )
          );

          // Also update in Kanban board if present
          if (kanbanBoard) {
            setKanbanBoard((prevBoard) => {
              const updatedBoard = { ...prevBoard };
              Object.keys(updatedBoard).forEach((status) => {
                updatedBoard[status] = updatedBoard[status].map((task) =>
                  task._id === taskId
                    ? { ...task, attachments: response.data }
                    : task
                );
              });
              return updatedBoard;
            });
          }

          return response.data;
        }
      } catch (err) {
toast.error(err.message || "Failed to attach file");
        throw err;
      }
    },
    [kanbanBoard]
  );

  /**
   * Socket event handlers
   */
  useEffect(() => {
    if (!currentRoomId) return;

    // Handle task created
    const handleTaskCreated = (data) => {
      if (data.roomId === currentRoomId) {
fetchKanbanBoard(currentRoomId);
      }
    };

    // Handle task updated
    const handleTaskUpdated = (data) => {
      if (data.roomId === currentRoomId) {
fetchKanbanBoard(currentRoomId);
      }
    };

    // Handle task status changed
    const handleTaskStatusChanged = (data) => {
      if (data.roomId === currentRoomId) {
fetchKanbanBoard(currentRoomId);
      }
    };

    // Handle task deleted
    const handleTaskDeleted = (data) => {
      if (data.roomId === currentRoomId) {
fetchKanbanBoard(currentRoomId);
      }
    };

    // Subscribe to socket events
    const unsubscribeCreated = roomSocketService.on(
      "task_created",
      handleTaskCreated
    );
    const unsubscribeUpdated = roomSocketService.on(
      "task_updated",
      handleTaskUpdated
    );
    const unsubscribeStatusChanged = roomSocketService.on(
      "task_status_changed",
      handleTaskStatusChanged
    );
    const unsubscribeDeleted = roomSocketService.on(
      "task_deleted",
      handleTaskDeleted
    );

    // Cleanup
    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeStatusChanged();
      unsubscribeDeleted();
    };
  }, [currentRoomId, fetchKanbanBoard]);

  const value = {
    // State
    tasks,
    kanbanBoard,
    myTasks,
    stats,
    loading,
    error,
    currentRoomId,

    // Actions
    fetchTasks,
    fetchMyTasks,
    fetchKanbanBoard,
    fetchTaskStats,
    createTask,
    updateTask,
    updateTaskStatus,
    deleteTask,
    toggleSubtask,
    addTaskComment,
    attachFileToTask,

    // Setters
    setTasks,
    setKanbanBoard,
    setCurrentRoomId,
  };

  return (
    <RoomTasksContext.Provider value={value}>
      {children}
    </RoomTasksContext.Provider>
  );
};

export default RoomTasksContext;
